import { setKeyboardUser } from "$json/lib/a11y-user-detect";
import {
  tabbableSelector,
  tabbableSelectorIncludingNegIndexed
} from "$json/lib/keyboard";
import * as R from "ramda";
import { adjacent, lastWithin, nearest, next } from "./dom.js";
import { custom } from "./events.js";
import { eventKey } from "./keys.js";
export {
  isKeyboardUser,
  isMouseUser,
  setKeyboardUser,
  setMouseUser
} from "$json/lib/a11y-user-detect";
export { tabbableSelector, tabbableSelectorIncludingNegIndexed };

const spatialMap = {
  ["ArrowUp"]: "previousNode",
  ["ArrowDown"]: "nextNode",
  ["ArrowLeft"]: "previousNode",
  ["ArrowRight"]: "nextNode"
};

/**
 * Key event handler focusing adjacent elements with arrows, container on ESCAPE, and firing custom 'select' event on ENTER
 * @param {Event} event
 * @param {Object} options
 * @param {String} options.selector
 */
export function navigateWithin(
  event,
  options = { selector: tabbableSelector }
) {
  const key = eventKey(event);
  if ("Enter" === key && event.currentTarget === event.target) {
    event.currentTarget.dispatchEvent(custom("select", null));
  }
  if ("Escape" === key) {
    event.currentTarget.focus();
  }
  if (["ArrowUp", "ArrowDown"].includes(key)) {
    const _focus = adjacent(
      event.currentTarget,
      event.target,
      spatialMap[key],
      options.selector
    );
    if (_focus && _focus !== event.target) {
      _focus.focus();
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }
}
/**
 * Key event handler focusing adjacent elements with arrows, container on ESCAPE,
 * firing custom 'select' event on ENTER, and bind (S-Tab) to move out of the container
 * @param {Event} event
 * @param {Object} options
 * @param {String} options.selector
 */
export function navigateWithinAndOutOf(
  event,
  options = { selector: tabbableSelector }
) {
  const key = eventKey(event);
  if ("Enter" === key && event.currentTarget === event.target) {
    event.currentTarget.dispatchEvent(custom("select", null));
  }
  if ("Escape" === key) {
    event.currentTarget.focus();
  }
  if (["ArrowUp", "ArrowDown"].includes(key)) {
    const _focus = adjacent(
      event.currentTarget,
      event.target,
      spatialMap[key],
      options.selector
    );
    if (_focus && _focus !== event.target) {
      _focus.focus();
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }
  if ("Tab" === key) {
    const prevent = tabNext(event.currentTarget);
    if (prevent) event.preventDefault();
  }
  if ("S-Tab" === key) {
    const prevent = tabPrev(event.currentTarget);
    if (prevent) event.preventDefault();
  }
}
const defaultVerticalSelectorFn = node =>
  `[data-action=${node.dataset["action"]}]:not([disabled])`;

/**
 * Key event handler focusing adjacent elements with arrows and container on ESCAPE
 * @param {Event} event
 * @param {Object} options
 * @param {String} options.horizontalSelector Selector for selectable nodes in the horizontal direction
 * @param {Function} options.verticalSelectorFn Given the target node, provide
 * string to act as a selector for selectable nodes in the vertical direction
 * @param {String} options.itemSelector Selector to get a node up the tree that is the root for the current item in the list
 */
export function navigateKindaSpatially(event, options) {
  const key = eventKey(event);
  const { horizontalSelector, itemSelector, verticalSelectorFn } =
    Object.assign(
      {},
      {
        horizontalSelector: tabbableSelectorIncludingNegIndexed,
        itemSelector: "li",
        verticalSelectorFn: defaultVerticalSelectorFn
      },
      options
    );
  if ("Enter" === key && event.currentTarget === event.target) {
    event.currentTarget.dispatchEvent(custom("select", null));
  }
  if ("Escape" === key) {
    event.currentTarget.focus();
  }
  if (R.has(key, spatialMap)) {
    const [root, selector] = ["ArrowUp", "ArrowDown"].includes(key)
      ? [event.currentTarget, verticalSelectorFn(event.target)]
      : [event.target.closest(itemSelector), horizontalSelector];
    const _focus = adjacent(root, event.target, spatialMap[key], selector);
    if (_focus && _focus !== event.target) {
      _focus.focus();
      setKeyboardUser();
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }
}

export const navigateInlineTo = selector => event => {
  const key = eventKey(event);
  if (["ArrowRight", "ArrowLeft"].includes(key)) {
    const { direction } = window.getComputedStyle(event.target);
    const previousKey = direction === "rtl" ? "ArrowRight" : "ArrowLeft";
    const nextKey = direction === "rtl" ? "ArrowLeft" : "ArrowRight";
    const methodMap = {
      [previousKey]: "previousNode",
      [nextKey]: "nextNode"
    };
    const _focus = adjacent(
      event.currentTarget,
      event.target,
      methodMap[key],
      selector
    );
    if (_focus) {
      _focus.focus();
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }
};

export const navigateToolbarOrTabs = navigateInlineTo(
  tabbableSelectorIncludingNegIndexed
);

/**
 * Key event handler focusing either next element matching focusableInItemSelector in itemSelector or dispatching a pre-made event on the target
 * @param {string} itemSelector
 * @param {string} focusableInItemSelector
 * @param {event} dispatchableEvent
 */
export const navigateNextOrDispatch =
  (
    itemSelector = "li",
    focusableInItemSelector = "input, textarea",
    dispatchableEvent
  ) =>
  event => {
    if ("Enter" !== eventKey(event)) return;
    if (!event.target.matches(focusableInItemSelector)) return;
    event.preventDefault();
    let item = nearest(itemSelector, event.target);
    let index = R.findIndex(node => node.contains(event.target))(item.children);
    let adj = next(event.currentTarget, item, "nextNode", itemSelector);
    if (adj !== item) {
      let focusable = adj.children[index].querySelector(
        focusableInItemSelector
      );
      focusable && focusable.focus();
    } else {
      event.target.dispatchEvent(dispatchableEvent);
    }
    event.stopPropagation();
  };

export const tabNext = root => {
  const last = lastWithin(root, tabbableSelector);
  let tabto = next(document.body, last, "nextNode", tabbableSelector);
  let tries = 0;
  if (tabto === last) return false;
  tabto.focus();
  while (tabto !== document.activeElement && tries < 10000) {
    tries++;
    tabto = next(document.body, tabto, "nextNode", tabbableSelector);
    if (tabto === last) return false;
    tabto.focus();
  }
  return true;
};

export const tabPrev = root => {
  const last = root;
  let tabto = next(document.body, last, "previousNode", tabbableSelector);
  if (tabto === last) return false;
  tabto.focus();
  let tries = 0;
  tabto.focus();
  while (tabto !== document.activeElement && tries < 10000) {
    tries++;
    tabto = next(document.body, tabto, "previousNode", tabbableSelector);
    if (tabto === last) return false;
    tabto.focus();
  }
  return true;
};

export const isEnterEvent = R.compose(R.equals("Enter"), eventKey);

export const interceptTabInTextPlusSubmitForm = R.when(
  R.either(
    R.both(
      R.compose(R.equals("Tab"), eventKey),
      R.pathEq(["target", "type"], "text")
    ),
    R.both(
      R.compose(R.equals("S-Tab"), eventKey),
      R.pathEq(["target", "type"], "submit")
    )
  ),
  R.tap(event => {
    event.stopPropagation();
  })
);

export const interceptTabIn_text_select_submit_form = R.compose(
  R.when(
    // R.both(
    //   R.complement(R.prop("defaultPrevented")),
    R.either(
      R.both(
        R.compose(R.equals("Tab"), eventKey),
        R.pathEq(["target", "type"], "select-one")
      ),
      R.both(
        R.compose(R.equals("S-Tab"), eventKey),
        R.pathEq(["target", "type"], "select-one")
      )
    ),
    // ),
    R.tap(event => {
      event.stopPropagation();
    })
  ),
  R.tap(e => {
    console.log(R.path(["target", "type"])(e));
  }),
  interceptTabInTextPlusSubmitForm
);
